import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { GetProgressionFn } from '@/features/jobs/details/tax-type/ProgressionViewModel'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { DocumentsListViewModelFactory } from '@/features/docs'
import { toStream } from '@/utils/mobx-util'
import { filter, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { computed } from 'mobx'

/**
 * Responsible for pulling the filed return document when the progression
 * has reached the "return filed" stage.
 */
export class FinalReturnViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _finalReturnDocumentsListVm

  constructor(
    public readonly job: SubmittedJob,
    getProgression: GetProgressionFn,
    createDocumentsListViewModel: DocumentsListViewModelFactory,
  ) {
    this._finalReturnDocumentsListVm = createDocumentsListViewModel({
      types: ['FINAL_RETURN'],
      limit: 1,
      context: {
        type: 'Job',
        jobId: this.job.id,
      },
    })
    this._lifecycle.manage(this._finalReturnDocumentsListVm)

    this._lifecycle.manage(
      toStream(() => getProgression()?.current === 'ReturnFiled', true).pipe(
        filter(Boolean),
        switchMap(() =>
          withAbortSignal((signal) => this._fetchFinalReturnDocument(signal)),
        ),
      ),
    )
  }

  /**
   * The final return document VM, if any.
   */
  @computed get finalReturnDocumentVm() {
    const vm = this._finalReturnDocumentsListVm.allDocuments.find(
      (d) => d.doc.typeId === 'FINAL_RETURN',
    )

    if (!vm) {
      return null
    }

    if (vm.doc.file?.state !== 'Downloadable') {
      return null
    }

    return vm
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the final return document.
   *
   * @param signal
   * @private
   */
  private async _fetchFinalReturnDocument(signal?: AbortSignal) {
    await this._finalReturnDocumentsListVm.fetch(signal)
  }
}
